import type { Screen } from './store.js';

/**
 * The hash router, as a module with no DOM in it.
 *
 * It used to be two functions halfway down index.ts, next to the `location`
 * writes and the boot sequence, which meant the only way to check that a hash
 * parsed the way it is written was to run a browser. Every route below is a
 * URL a person can paste into a bug report — "the Access list for kino-vela",
 * "traffic for the last seven days" — so the round trip between a route and its
 * hash is a behaviour, and a behaviour needs a test that does not need Chrome.
 *
 * TWO SPELLINGS OF EVERY OLD ROUTE. `#/fleet`, `#/tenant/<id>`, `#/usage` and
 * `#/onboard` are in browser histories and in at least one screenshot, so they
 * parse to the destinations that replaced them rather than 404ing to the front
 * page; `hashFor` only ever writes the new spelling, so a bookmark upgrades
 * itself the first time it is used. `#/connect` is the one route with no
 * successor of its own — its contents are now a tab of each tenant — so it
 * lands on Sites, which is where a person goes to choose one. `#/billing` is
 * the same kind of move: billing is a section of Settings now.
 *
 * THE FRONT PAGE IS OVERVIEW. An empty hash, `#/` and any unknown head land
 * there, and `hashFor` writes it as `#/` — the root of the console is the
 * shortest address it has. Sites keeps `#/sites`.
 *
 * AN UNKNOWN TENANT TAB IS `overview`, NOT A BLANK SCREEN. A typo in a tab
 * segment, or a link written against a tab that has since been renamed, is a
 * person who still meant to look at that tenant.
 */

/**
 * The tenant's own tabs.
 *
 * A tenant is an object with parts, not a 300-line scroll: this is the list of
 * its parts, in the order the questions get asked — is it up, what does it
 * advertise, how do I reach it, who can touch it, and how do I take it down.
 *
 * "What does it look like" is deliberately NOT among them any more. It was a
 * tab, a screen away from the tools it described, and the two are one question:
 * somebody configuring a tool wants to see what configuring it did. The preview
 * and the slot editor sit inside each tool now. `appearance` stays in the union
 * as a redirect target, because it was a pasteable address for a while and a
 * bookmark should open the thing it used to open rather than nothing.
 */
export type TenantTab = 'overview' | 'tools' | 'appearance' | 'connect' | 'access' | 'settings';

export const TENANT_TABS: readonly { tab: TenantTab; label: string }[] = [
  { tab: 'overview', label: 'Overview' },
  { tab: 'tools', label: 'Tools' },
  { tab: 'connect', label: 'Connect' },
  { tab: 'access', label: 'Access' },
  { tab: 'settings', label: 'Settings' },
];

const TAB_NAMES = new Set<string>(TENANT_TABS.map((t) => t.tab));

export function isTenantTab(value: string): value is TenantTab {
  return TAB_NAMES.has(value);
}

export interface Route {
  screen: Screen;
  tenantId: string | null;
  /** Which part of the tenant. Meaningless on every other screen; never null. */
  tenantTab: TenantTab;
  /**
   * `#/crawl/<jobId>` is a crawl in progress or a draft awaiting review. The
   * id is in the hash for the same reason the tenant id is: a reload mid-crawl
   * must land back on the job, not on an empty form, and the job record is a
   * plain read of server state that can be re-fetched at any moment.
   */
  jobId: string | null;
  usageDays: number;
  usageTenantId: string | null;
  /**
   * `#/login?error=expired` is where the callback sends a dead link. The word
   * is the server's; it is turned into a sentence by `loginNoticeFor` and shown
   * above the form, because a person who clicked a stale email needs to be told
   * to ask for another rather than left looking at a form they already filled.
   */
  loginError: string | null;
}

export const DEFAULT_DAYS = 30;

/**
 * Screen state lives in the hash, because losing your place on reload was a
 * real complaint about the onboarding console — which persists nothing and
 * cannot, since a streaming crawl has no resumable URL. This page has the
 * opposite property: every screen is a plain read of server state, so a reload,
 * a back button and a shared link can all land exactly where the operator was.
 * Who you are is NOT part of that: a reload asks the server, and a signed-out
 * reload lands on the login screen with the destination remembered, since the
 * destination is worthless without a principal the server will answer to.
 */
export function parseRoute(hash: string, fallbackDays: number): Route {
  const raw = hash.replace(/^#\/?/, '');
  const [pathPart = '', queryPart = ''] = raw.split('?');
  const segments = pathPart.split('/').filter(Boolean).map(decodeURIComponent);
  const query = new URLSearchParams(queryPart);
  const days = Number(query.get('days'));

  const route: Route = {
    screen: 'overview',
    tenantId: null,
    tenantTab: 'overview',
    jobId: null,
    usageDays: Number.isFinite(days) && days >= 1 && days <= 365 ? Math.round(days) : fallbackDays,
    usageTenantId: query.get('tenantId') || null,
    loginError: null,
  };

  const head = segments[0] ?? '';

  // `#/sites/<id>[/<tab>]` — and `#/tenant/<id>`, the spelling that shipped first.
  if ((head === 'sites' || head === 'fleet' || head === 'tenant') && segments[1]) {
    route.screen = 'tenant';
    route.tenantId = segments[1];
    const tab = segments[2] ?? '';
    if (tab && isTenantTab(tab)) route.tenantTab = tab;
    return route;
  }
  if (head === 'sites' || head === 'fleet' || head === 'connect') {
    route.screen = 'sites';
    return route;
  }
  if (head === 'crawl' || head === 'onboard') {
    route.screen = 'crawl';
    route.jobId = segments[1] ?? null;
    return route;
  }
  if (head === 'traffic' || head === 'usage') {
    route.screen = 'traffic';
    return route;
  }
  if (head === 'host') {
    route.screen = 'host';
    return route;
  }
  if (head === 'overview') return route;
  // Billing became a section of Settings; the old address opens the new place.
  if (head === 'settings' || head === 'billing') {
    route.screen = 'settings';
    return route;
  }
  if (head === 'login') {
    route.screen = 'login';
    route.loginError = query.get('error');
    return route;
  }
  return route;
}

/** The sentence for a `?error=` the callback sent. The server's word; our prose. */
export function loginNoticeFor(code: string | null): string | null {
  if (!code) return null;
  if (code === 'expired') {
    return 'That sign-in link has expired or was already used. Ask for a new one below; each link works once.';
  }
  return `Sign-in did not complete (${code}). Ask for a new link below.`;
}

export interface Destination {
  screen: Screen;
  tenantId?: string | null;
  tenantTab?: TenantTab | null;
  jobId?: string | null;
  usageDays?: number;
  usageTenantId?: string | null;
}

export function hashFor(route: Destination): string {
  if (route.screen === 'tenant' && route.tenantId) {
    const id = encodeURIComponent(route.tenantId);
    // Overview is the tenant itself, so it has the shorter URL: `#/sites/x` and
    // `#/sites/x/overview` are the same place and only one of them is written.
    return route.tenantTab && route.tenantTab !== 'overview'
      ? `#/sites/${id}/${route.tenantTab}`
      : `#/sites/${id}`;
  }
  if (route.screen === 'crawl') {
    return route.jobId ? `#/crawl/${encodeURIComponent(route.jobId)}` : '#/crawl';
  }
  if (route.screen === 'traffic') {
    const q = new URLSearchParams();
    if (route.usageDays && route.usageDays !== DEFAULT_DAYS) q.set('days', String(route.usageDays));
    if (route.usageTenantId) q.set('tenantId', route.usageTenantId);
    const s = q.toString();
    return s ? `#/traffic?${s}` : '#/traffic';
  }
  if (route.screen === 'host') return '#/host';
  if (route.screen === 'settings') return '#/settings';
  if (route.screen === 'login') return '#/login';
  if (route.screen === 'sites') return '#/sites';
  return '#/';
}
